/**
 * The phone's live view of its store: the downloaded store cache with this phone's own F5
 * commands laid on top (those made after the download), plus what sync said about them.
 *
 * PURE: `projectStore(stored, outbox)` → StoreState. The server stays the authority — it re-checks
 * every rule when a command is applied and the next cache download replaces this view.
 */
import type { OutboxRecord } from '@/offline/db'
import type {
  BayDeclaration,
  DeclarationForm,
  StoreBag,
  StoreCache,
  StoreLocation,
  StoreLot,
  StoreSample,
  StoredStoreCache,
  Trip,
} from '@/offline/storeCache'
import type {
  CountBayPayload,
  DispatchShipmentPayload,
  LoadBagsPayload,
  MarkBagsConvPayload,
  PutAwayPayload,
  ReceiveShipmentPayload,
  ReplaceContainerPayload,
  SignBayDeclarationPayload,
} from './commands'
import { STORAGE_FORM_CODE, STORE_COMMAND_TYPES } from './commands'

/** A bag as the phone sees it now. */
export interface BagView extends StoreBag {
  /** The not-yet-synced command that last changed this bag (a later command depends on it). */
  pending_command_id: string | null
  /** Exception ids when sync answered HELD for a command touching this bag. */
  sync_exception_ids: string[]
  /** A command touching this bag is in ERROR on the phone. */
  sync_error: boolean
}

export interface SampleView extends StoreSample {
  pending_command_id: string | null
}

export interface TripView extends Trip {
  /** Made on this phone (load) and not in the downloaded cache. */
  local: boolean
  /** Dispatched / received on this phone. */
  local_status: Trip['status'] | null
  hand_count?: number
}

export interface LocalCount {
  count_id: string
  command_id: string
  location_id: string
  qr_payloads: string[]
  ended_at: string
  done: boolean
}

export interface LocalReplacement {
  command_id: string
  status: OutboxRecord['status']
  payload: ReplaceContainerPayload
  /** From the server's answer (server_state.evidence) once synced. */
  evidence: 'OLD_TAG' | 'COUNT_AND_WEIGHT' | 'NONE' | null
  created_at: number
}

export interface StoreState {
  cache: StoreCache
  downloadedAt: number
  siteId: string
  locations: Map<string, StoreLocation>
  /** By label payload AND by printed code (typed fallback). */
  locationByCode: Map<string, StoreLocation>
  lots: Map<string, StoreLot>
  bags: Map<string, BagView>
  bagByPayload: Map<string, BagView>
  samples: Map<string, SampleView>
  sampleByPayload: Map<string, SampleView>
  deadTags: Map<string, 'VOID' | 'RETIRED'>
  /** Bay id → declaration in force (latest, local signings included). */
  declarations: Map<string, BayDeclaration & { local: boolean }>
  trips: Map<string, TripView>
  counts: Map<string, LocalCount>
  replacements: LocalReplacement[]
  /** Tag payloads bound on this phone since the download (replacement sacks). */
  usedTags: Set<string>
  form: DeclarationForm
}

/** STORAGE_NO_FUMIGANT v1 (F5.4 screen), used when the cache carries no form. */
export const DEFAULT_STORAGE_FORM: DeclarationForm = {
  form_code: STORAGE_FORM_CODE,
  version: 1,
  questions: [
    {
      key: 'fumigant_in_bay',
      hi: '180 दिन में इस बे में फ्यूमिगेशन?',
      en: 'Fumigant used in this bay in the last 180 days?',
      kind: 'yes_no',
      clean_answer: false,
    },
    {
      key: 'fumigated_stack_on_site',
      hi: 'गोदाम में कहीं फ्यूमिगेशन वाला ढेर?',
      en: 'Any fumigated stack on the site?',
      kind: 'yes_no',
      clean_answer: null,
    },
    {
      key: 'distance_to_fumigated_stack_m',
      hi: 'दूरी (मीटर)',
      en: 'Distance to the nearest one (m)',
      kind: 'number',
      required_if: 'fumigated_stack_on_site',
      unit: 'm',
    },
    {
      key: 'conv_produce_in_bay',
      hi: 'बे में लाल-टाई माल?',
      en: 'Red-tie (ordinary) produce in the bay?',
      kind: 'yes_no',
      clean_answer: false,
    },
    {
      key: 'pesticide_on_premises',
      hi: 'गोदाम में कीटनाशक रखा है?',
      en: 'Pesticide stored on the premises?',
      kind: 'yes_no',
      clean_answer: false,
    },
  ],
}

const STORE_TYPES: ReadonlySet<string> = new Set(STORE_COMMAND_TYPES)
const DONE: ReadonlySet<OutboxRecord['status']> = new Set(['APPLIED', 'DUPLICATE', 'HELD'])

const addDays = (iso: string, days: number) =>
  new Date(new Date(iso).getTime() + days * 86_400_000).toISOString()

/** clean = every yes_no question with a clean answer got that answer (F5.4). */
export function isCleanAnswers(form: DeclarationForm, answers: Record<string, unknown>): boolean {
  return form.questions.every(
    (q) =>
      q.kind !== 'yes_no' ||
      q.clean_answer === null ||
      q.clean_answer === undefined ||
      answers[q.key] === q.clean_answer,
  )
}

export function projectStore(
  stored: StoredStoreCache,
  outbox: readonly OutboxRecord[],
): StoreState {
  const { cache, downloaded_at } = stored
  const form = cache.forms.find((f) => f.form_code === STORAGE_FORM_CODE) ?? DEFAULT_STORAGE_FORM

  const locations = new Map(cache.locations.map((l) => [l.id, l]))
  const locationByCode = new Map<string, StoreLocation>()
  for (const l of cache.locations) {
    locationByCode.set(l.code.toUpperCase(), l)
    if (l.qr_payload) locationByCode.set(l.qr_payload.toUpperCase(), l)
  }
  const lots = new Map(cache.lots.map((l) => [l.id, { ...l }]))
  const bags = new Map<string, BagView>(
    cache.bags.map((b) => [
      b.container_id,
      { ...b, pending_command_id: null, sync_exception_ids: [], sync_error: false },
    ]),
  )
  const samples = new Map<string, SampleView>(
    cache.samples.map((s) => [s.sample_id, { ...s, pending_command_id: null }]),
  )
  const deadTags = new Map(cache.dead_tags.map((d) => [d.qr_payload.toUpperCase(), d.status]))
  const declarations = new Map<string, BayDeclaration & { local: boolean }>()
  for (const d of cache.declarations) {
    const prev = declarations.get(d.location_id)
    if (!prev || prev.signed_at < d.signed_at)
      declarations.set(d.location_id, { ...d, local: false })
  }
  const trips = new Map<string, TripView>(
    cache.trips.map((t) => [t.shipment_id, { ...t, local: false, local_status: null }]),
  )
  const counts = new Map<string, LocalCount>()
  const replacements: LocalReplacement[] = []
  const usedTags = new Set<string>()

  const byPayload = () => {
    const m = new Map<string, BagView>()
    for (const b of bags.values()) m.set(b.qr_payload.toUpperCase(), b)
    return m
  }
  let bagByPayload = byPayload()

  const records = [...outbox]
    .filter((r) => STORE_TYPES.has(r.type) && r.created_at > downloaded_at)
    .sort((a, b) => (a.seq ?? 0) - (b.seq ?? 0))

  for (const rec of records) {
    if (rec.status === 'ERROR') {
      markBags(rec, (b) => (b.sync_error = true))
      continue
    }
    const pending = DONE.has(rec.status) ? null : rec.id
    const held = rec.status === 'HELD' ? (rec.result?.exception_ids ?? []) : []
    const touch = (b: BagView | undefined) => {
      if (!b) return
      b.pending_command_id = pending
      if (held.length) b.sync_exception_ids = [...b.sync_exception_ids, ...held]
    }

    switch (rec.type) {
      case 'load_bags': {
        const p = rec.payload as LoadBagsPayload
        const items: Trip['items'] = []
        for (const it of p.items) {
          if (it.container_id) {
            const b = bags.get(it.container_id)
            if (b) {
              b.shipment_id = p.shipment.id
              touch(b)
            }
          } else if (it.sample_id) {
            const s = samples.get(it.sample_id)
            if (s) {
              s.shipment_id = p.shipment.id
              s.pending_command_id = pending
            }
          }
          items.push({
            item_id: it.id,
            container_id: it.container_id ?? null,
            sample_id: it.sample_id ?? null,
            qr_payload: it.qr_payload,
            unit_id: it.container_id ? (bags.get(it.container_id)?.unit_id ?? null) : null,
            recheck_required: false,
          })
        }
        trips.set(p.shipment.id, {
          shipment_id: p.shipment.id,
          code: p.shipment.code,
          kind: 'TRANSFER',
          status: 'LOADING',
          from_location_id: p.shipment.from_location_id,
          to_location_id: p.shipment.to_location_id,
          to_name: cache.destinations.find((d) => d.id === p.shipment.to_location_id)?.name,
          vehicle_no: p.shipment.vehicle_no,
          challan_no: p.shipment.challan_no,
          items,
          local: true,
          local_status: 'LOADING',
        })
        break
      }
      case 'dispatch_shipment': {
        const p = rec.payload as DispatchShipmentPayload
        const trip = trips.get(p.shipment_id)
        if (trip) {
          trip.local_status = 'IN_TRANSIT'
          trip.status = 'IN_TRANSIT'
          trip.dispatched_at = p.dispatched_at
          trip.hand_count = p.hand_count
          for (const it of trip.items) {
            const b = it.container_id ? bags.get(it.container_id) : undefined
            if (b) {
              b.status = 'IN_TRANSIT'
              b.location_id = null
              touch(b)
            }
            const s = it.sample_id ? samples.get(it.sample_id) : undefined
            if (s) {
              s.status = 'IN_TRANSIT'
              s.location_id = null
            }
          }
        }
        break
      }
      case 'receive_shipment': {
        const p = rec.payload as ReceiveShipmentPayload
        const trip = trips.get(p.shipment_id)
        const dest = trip?.to_location_id ?? cache.site_id
        const itemById = new Map((trip?.items ?? []).map((i) => [i.item_id, i]))
        const arrive = (b: BagView | undefined, at: string) => {
          if (!b) return
          b.status = 'STORED'
          b.location_id = dest
          b.shipment_id = null
          b.in_since = at
          touch(b)
        }
        for (const u of p.unloads) {
          const it = itemById.get(u.item_id)
          if (it?.container_id) arrive(bags.get(it.container_id), u.unloaded_at)
          if (it?.sample_id) {
            const s = samples.get(it.sample_id)
            if (s) {
              s.status = 'SEALED'
              s.location_id = dest
              s.shipment_id = null
              s.pending_command_id = pending
            }
          }
        }
        for (const x of p.extras) {
          const b = bagByPayload.get(x.qr_payload.toUpperCase())
          if (b && b.status === 'MISSING') b.hold = 'QUARANTINE'
          arrive(b, x.unloaded_at)
        }
        for (const m of p.missing) {
          const it = itemById.get(m.item_id)
          const b = it?.container_id ? bags.get(it.container_id) : undefined
          if (b) {
            b.status = 'MISSING'
            b.location_id = null
            touch(b)
          }
        }
        for (const w of p.check_weights) {
          const b = bags.get(w.container_id)
          if (b) b.net_g_current = w.value_g - b.tare_g
        }
        if (trip) {
          trip.local_status = p.missing.length ? 'RECEIVED_SHORT' : 'RECEIVED'
          trip.status = trip.local_status
        }
        break
      }
      case 'put_away':
      case 'move_bags': {
        const p = rec.payload as PutAwayPayload
        for (const it of p.items) {
          if (it.container_id) {
            const b = bags.get(it.container_id)
            if (b) {
              if (b.location_id !== p.location_id) b.in_since = it.scanned_at
              b.location_id = p.location_id
              if (b.status === 'MISSING') {
                b.status = 'STORED'
                b.hold = 'QUARANTINE'
              }
              touch(b)
            }
          } else if (it.sample_id) {
            const s = samples.get(it.sample_id)
            if (s) {
              s.location_id = p.location_id
              s.status = 'IN_STORE'
              s.pending_command_id = pending
            }
          }
        }
        break
      }
      case 'mark_bags_conv': {
        const p = rec.payload as MarkBagsConvPayload
        for (const it of p.items) {
          const b = bags.get(it.container_id)
          if (b) {
            b.stream = 'CONV'
            b.red_tie_pending = false
            touch(b)
          }
        }
        break
      }
      case 'sign_bay_declaration': {
        const d = (rec.payload as SignBayDeclarationPayload).declaration
        declarations.set(d.subject_id, {
          id: d.id,
          location_id: d.subject_id,
          form_code: d.form_code,
          form_version: d.form_version,
          clean: isCleanAnswers(form, d.answers),
          signed_at: d.signed_at,
          valid_to: addDays(d.signed_at, cache.settings.declaration_renewal_days),
          answers: d.answers,
          local: true,
        })
        break
      }
      case 'count_bay': {
        const c = (rec.payload as CountBayPayload).count
        counts.set(c.location_id, {
          count_id: c.id,
          command_id: rec.id,
          location_id: c.location_id,
          qr_payloads: c.qr_payloads,
          ended_at: c.ended_at,
          done: pending === null,
        })
        break
      }
      case 'replace_container': {
        const p = rec.payload as ReplaceContainerPayload
        const old = p.old_qr_payload ? bagByPayload.get(p.old_qr_payload.toUpperCase()) : undefined
        const state = rec.result?.server_state as
          { evidence?: LocalReplacement['evidence'] } | undefined
        usedTags.add(p.new_container.qr_payload.toUpperCase())
        if (old) {
          old.status = 'VOID'
          old.location_id = null
          touch(old)
          deadTags.set(old.qr_payload.toUpperCase(), 'VOID')
        }
        const sack = cache.sack_types.find((s) => s.id === p.new_container.sack_type_id)
        const tare = sack?.tare_g ?? 0
        const nb: BagView = {
          container_id: p.new_container.id,
          qr_payload: p.new_container.qr_payload,
          tag_kind: 'BAG',
          // Unknown until the server decides the evidence (a lost tag may become an orphan).
          unit_id: old?.unit_id ?? '',
          status: 'STORED',
          hold: 'QUARANTINE',
          stream: old?.stream ?? 'MAIN',
          location_id: p.location_id,
          shipment_id: null,
          net_g_gate: old?.net_g_gate ?? null,
          net_g_current: p.measurement.value_g - tare,
          tare_g: tare,
          gate_weigher_id: null,
          in_since: p.measurement.taken_at,
          red_tie_pending: false,
          pending_command_id: pending,
          sync_exception_ids: held,
          sync_error: false,
        }
        bags.set(nb.container_id, nb)
        replacements.push({
          command_id: rec.id,
          status: rec.status,
          payload: p,
          evidence: state?.evidence ?? null,
          created_at: rec.created_at,
        })
        break
      }
    }
    bagByPayload = byPayload()
  }

  function markBags(rec: OutboxRecord, fn: (b: BagView) => void) {
    const p = rec.payload as { items?: Array<{ container_id?: string }> }
    for (const it of p.items ?? []) {
      const b = it.container_id ? bags.get(it.container_id) : undefined
      if (b) fn(b)
    }
  }

  const sampleByPayload = new Map<string, SampleView>()
  for (const s of samples.values()) sampleByPayload.set(s.seal_payload.toUpperCase(), s)

  return {
    cache,
    downloadedAt: downloaded_at,
    siteId: cache.site_id,
    locations,
    locationByCode,
    lots,
    bags,
    bagByPayload,
    samples,
    sampleByPayload,
    deadTags,
    declarations,
    trips,
    counts,
    replacements,
    usedTags,
    form,
  }
}

// ---------------------------------------------------------------------------------------------
// Place helpers
// ---------------------------------------------------------------------------------------------

/** The location itself and its ancestors, nearest first. */
export function ancestors(state: Pick<StoreState, 'locations'>, id: string | null | undefined) {
  const out: StoreLocation[] = []
  let cur = id ? state.locations.get(id) : undefined
  const seen = new Set<string>()
  while (cur && !seen.has(cur.id)) {
    out.push(cur)
    seen.add(cur.id)
    cur = cur.parent_id ? state.locations.get(cur.parent_id) : undefined
  }
  return out
}

/** The BAY a place belongs to (a spot uses its bay's declaration). */
export function bayOf(state: Pick<StoreState, 'locations'>, id: string | null | undefined) {
  return ancestors(state, id).find((l) => l.level === 'BAY') ?? null
}

export function siteOf(state: Pick<StoreState, 'locations'>, id: string | null | undefined) {
  return ancestors(state, id).find((l) => l.level === 'SITE') ?? null
}

/** "Godown A › Bay 3 › S11-P04". */
export function placePath(state: Pick<StoreState, 'locations'>, id: string | null | undefined) {
  const loc = id ? state.locations.get(id) : undefined
  if (!loc) return ''
  if (loc.path) return loc.path
  return ancestors(state, id)
    .reverse()
    .map((l) => l.name ?? l.code)
    .join(' › ')
}

/** Every place at or below `id`. */
export function descendantIds(state: Pick<StoreState, 'locations'>, id: string): Set<string> {
  const out = new Set([id])
  let grew = true
  while (grew) {
    grew = false
    for (const l of state.locations.values()) {
      if (l.parent_id && out.has(l.parent_id) && !out.has(l.id)) {
        out.add(l.id)
        grew = true
      }
    }
  }
  return out
}

export type DeclarationStatus = 'CLEAN' | 'NOT_CLEAN' | 'EXPIRED' | 'OLD_FORM' | 'NONE'

export interface DeclarationInfo {
  status: DeclarationStatus
  declaration: (BayDeclaration & { local: boolean }) | null
  /** Whole days until valid_to (negative when expired). */
  daysLeft: number | null
  /** Expires within the warning window. */
  renewSoon: boolean
}

/** Is the bay's STORAGE_NO_FUMIGANT declaration in force and clean (F5.4)? */
export function declarationInfo(
  state: Pick<StoreState, 'locations' | 'declarations' | 'form' | 'cache'>,
  locationId: string | null | undefined,
  now: number,
): DeclarationInfo {
  const bay = bayOf(state, locationId)
  const d = bay ? (state.declarations.get(bay.id) ?? null) : null
  if (!d) return { status: 'NONE', declaration: null, daysLeft: null, renewSoon: false }
  const daysLeft = Math.floor((new Date(d.valid_to).getTime() - now) / 86_400_000)
  const renewSoon = daysLeft >= 0 && daysLeft < state.cache.settings.declaration_warn_days
  let status: DeclarationStatus
  if (new Date(d.valid_to).getTime() <= now) status = 'EXPIRED'
  else if (d.form_version < state.form.version) status = 'OLD_FORM'
  else status = d.clean ? 'CLEAN' : 'NOT_CLEAN'
  return { status, declaration: d, daysLeft, renewSoon }
}

export type Availability = 'AVAILABLE' | 'ON_HOLD' | 'ORDINARY' | 'IN_TRANSIT' | 'NOT_AVAILABLE'

/**
 * F5.6: available = lot committed, stream MAIN, lot hold NONE, bag hold NONE, bag STORED, in a bay
 * with a clean declaration in force. Held → ON_HOLD (shown with the reason); CONV/orphan → ORDINARY.
 */
export function availability(
  state: Pick<StoreState, 'locations' | 'declarations' | 'form' | 'cache' | 'lots'>,
  bag: BagView,
  now: number,
): Availability {
  const lot = state.lots.get(bag.unit_id)
  if (bag.stream === 'CONV' || lot?.stream === 'CONV' || lot?.unit_type === 'ORPHAN')
    return 'ORDINARY'
  if (bag.hold !== 'NONE' || (lot && lot.hold !== 'NONE') || bag.sync_exception_ids.length)
    return 'ON_HOLD'
  if (bag.status === 'IN_TRANSIT' || bag.shipment_id) return 'IN_TRANSIT'
  if (bag.status !== 'STORED') return 'NOT_AVAILABLE'
  return declarationInfo(state, bag.location_id, now).status === 'CLEAN'
    ? 'AVAILABLE'
    : 'NOT_AVAILABLE'
}

/** Bags (live, not void/missing/issued) with an open place at `locationId` or below. */
export function bagsAt(state: StoreState, locationId: string, includeBelow = true): BagView[] {
  const ids = includeBelow ? descendantIds(state, locationId) : new Set([locationId])
  return [...state.bags.values()].filter(
    (b) =>
      b.location_id &&
      ids.has(b.location_id) &&
      !b.shipment_id &&
      (b.status === 'STORED' || b.status === 'SEALED'),
  )
}

export function lotLabel(state: Pick<StoreState, 'lots'>, unitId: string): string {
  return state.lots.get(unitId)?.code ?? '—'
}
